import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Text } from "@krea/customer-features/shared-components/text";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { NumberTicker } from "@krea/shared/design-system/number-ticker";
import { CircularProgressBar } from "@krea/shared/design-system/progress/circular-progress-bar";

export const AttractivenessComponent = ({
  className,
}: {
  className: string;
}) => {
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();
  const { forecastApprovedProbability } = data ?? {};
  const [value, setValue] = useState(0);

  useEffect(() => {
    const handleIncrement = () => {
      return forecastApprovedProbability
        ? forecastApprovedProbability * 100
        : 0;
    };
    setValue(handleIncrement);
    const interval = setInterval(() => setValue(handleIncrement), 1000);

    return () => clearInterval(interval);
  }, [forecastApprovedProbability]);

  const isHighApprovedProbability = useMemo(() => {
    return forecastApprovedProbability && forecastApprovedProbability > 0.85;
  }, [forecastApprovedProbability]);

  return (
    <div
      className={twMerge(
        "tw-flex tw-p-5 tw-border tw-rounded-lg tw-items-center tw-gap-3",
        {
          "tw-border-orange tw-bg-orange-50": !isHighApprovedProbability,
          "tw-border-mint-300 tw-bg-mint-100": isHighApprovedProbability,
        },
        className,
      )}
    >
      <CircularProgressBar
        max={100}
        min={0}
        value={value}
        gaugeSecondaryColor="white"
        className={twMerge("tw-size-10", {
          "[&_circle#progress-bar-primary]:!tw-stroke-orange":
            !isHighApprovedProbability,
          "[&_circle#progress-bar-primary]:!tw-stroke-mint-300":
            isHighApprovedProbability,
        })}
      />
      <div>
        <Text size="sm" className="tw-font-semibold">
          {t("advice.digital.likelihood")}
        </Text>
        {forecastApprovedProbability && forecastApprovedProbability > -1 && (
          <Text
            size="md"
            className={twMerge("tw-font-semibold", {
              "tw-text-orange": !isHighApprovedProbability,
              "tw-text-mint-300": isHighApprovedProbability,
            })}
          >
            <NumberTicker value={forecastApprovedProbability * 100} extra="%" />
          </Text>
        )}
      </div>
    </div>
  );
};

export default AttractivenessComponent;
